import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  Col,
  Dropdown,
  Form,
  Modal,
  Nav,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  Database,
  Download,
  RefreshCw,
  Play,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Search,
  PlusCircle,
  Trash2,
  Activity,
  Upload,
  ChevronRight,
  ChevronDown,
} from "react-feather";
import { Link } from "react-router";
import { useGet, useGetLazy, usePost, useDelete } from "../../api";
import { usePermissions } from "../../hooks";
import {
  FullAutoCouponsTargetingCheckStatus,
  IFullAutoCouponsTargetingSnapshotsResponse,
  IFullAutoCouponsTargetingRunSummary,
  IFullAutoCouponsTargetingRunsResponse,
  IFullAutoCouponsTargetingActiveRunsResponse,
  IFullAutoCouponsTargetingRunDetail,
  IFullAutoCouponsTargetingBatch,
  IFullAutoCouponsTargetingTrackedItem,
  IFullAutoCouponsTargetingItemsResponse,
  IFullAutoCouponsTargetingAddItemResponse,
  IFullAutoCouponsTargetingResetResponse,
  IFullAutoCouponsTargetingLivePreviewResponse,
  IFullAutoCouponsTargetingImportResponse,
  IFullAutoCouponsTargetingDailyReportResponse,
  IFullAutoCouponsTargetingDailyReportEntry,
  IFullAutoCouponsTargetingTemplateCountsResponse,
  PermissionAction,
  PermissionResource,
  SDK_TEST_FRESH_DAYS,
  sdkTestCheckoutTimingLabel,
} from "../../interfaces";
import { SDKTestModal } from "./sdk-test-modal";
import { DomainGeoPicker, DomainGeoItem } from "../qa-lists/domain-geo-picker";

interface DatesResponse {
  dates: string[];
}
type RunsResponse = IFullAutoCouponsTargetingRunsResponse;

// ── Import Live Items Modal ──────────────────────────────

const csvEscape = (v: unknown): string => {
  const s = v == null ? "" : String(v);
  if (/["\n,]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const ImportLiveItemsModal: React.FC<{
  show: boolean;
  onClose: () => void;
  onImported: () => void;
}> = ({ show, onClose, onImported }) => {
  const [preview, setPreview] =
    useState<IFullAutoCouponsTargetingLivePreviewResponse | null>(null);
  const [importResult, setImportResult] =
    useState<IFullAutoCouponsTargetingImportResponse | null>(null);

  const {
    execute: fetchPreview,
    loading: previewLoading,
    error: previewError,
  } = useGetLazy<IFullAutoCouponsTargetingLivePreviewResponse>();

  const {
    execute: doImport,
    loading: importing,
    error: importError,
  } = usePost<IFullAutoCouponsTargetingImportResponse>();

  const handlePreview = async () => {
    setPreview(null);
    setImportResult(null);
    const data = await fetchPreview(
      "full-auto-coupons-targeting/import-preview"
    );
    if (data) setPreview(data);
  };

  const handleImport = async (triggerRun: boolean) => {
    const data = await doImport("full-auto-coupons-targeting/import", { triggerRun });
    if (data) {
      setImportResult(data);
      onImported();
    }
  };

  const handleExportCsv = () => {
    if (!preview) return;
    const headers = [
      "domain",
      "countryCode",
      "brandName",
      "liveCouponCount",
      "alreadyTracked",
    ];
    const lines = [
      headers.join(","),
      ...preview.items.map((r) =>
        [
          r.domain,
          r.countryCode,
          r.brandName,
          r.liveCouponCount,
          r.alreadyTracked,
        ]
          .map(csvEscape)
          .join(",")
      ),
    ];
    const blob = new Blob([lines.join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `live-import-preview-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    setPreview(null);
    setImportResult(null);
    onClose();
  };

  const error = previewError?.message ?? importError?.message ?? null;

  return (
    <Modal show={show} onHide={handleClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">Import live items</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="small text-muted mb-3">
          Finds all domain/country pairs in the Kindred SQL database that
          currently have an <strong>active checkout template</strong>, a{" "}
          <strong>live deal</strong> (approved, non-suspended campaign), and at
          least one <strong>live coupon code</strong> (approved, within validity
          dates). New pairs are added to the tracking list and snapshot runs are
          triggered immediately.
        </p>

        {!preview && !previewLoading && (
          <div className="text-center py-3">
            <Button variant="outline-primary" onClick={handlePreview}>
              <Search size={14} className="me-1" />
              Load preview
            </Button>
          </div>
        )}

        {previewLoading && (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <div className="small text-muted mt-2">Querying SQL database…</div>
          </div>
        )}

        {error && (
          <Alert variant="danger" className="small py-2">
            {error}
          </Alert>
        )}

        {importResult && (
          <Alert variant="success" className="small py-2 mb-3">
            <CheckCircle size={12} className="me-1" />
            Import complete — <strong>{importResult.imported}</strong> new item
            {importResult.imported !== 1 ? "s" : ""} added and snapshot runs
            triggered. <strong>{importResult.skipped}</strong> already tracked
            (skipped).
          </Alert>
        )}

        {preview && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="small">
                <strong>{preview.total}</strong> eligible items —{" "}
                <span className="text-success fw-semibold">
                  {preview.newCount} new
                </span>
                {preview.alreadyTrackedCount > 0 && (
                  <span className="text-muted ms-1">
                    , {preview.alreadyTrackedCount} already tracked
                  </span>
                )}
              </div>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={handleExportCsv}
              >
                <Download size={13} className="me-1" />
                Export CSV
              </Button>
            </div>

            <div style={{ maxHeight: 420, overflowY: "auto" }}>
              <Table size="sm" hover className="mb-0 small">
                <thead className="table-light sticky-top">
                  <tr>
                    <th>Domain</th>
                    <th>Country</th>
                    <th>Brand</th>
                    <th>Live coupons</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.items.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-3">
                        No eligible items found.
                      </td>
                    </tr>
                  ) : (
                    preview.items.map((item, i) => (
                      <tr key={i}>
                        <td>
                          <a
                            href={`https://${item.domain}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {item.domain}
                          </a>
                        </td>
                        <td>{item.countryCode}</td>
                        <td className="text-muted">{item.brandName ?? "—"}</td>
                        <td>
                          <Badge bg="primary">{item.liveCouponCount}</Badge>
                        </td>
                        <td>
                          {item.alreadyTracked ? (
                            <Badge bg="secondary">Already tracked</Badge>
                          ) : (
                            <Badge bg="success">New</Badge>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="py-2">
        <Button size="sm" variant="secondary" onClick={handleClose}>
          Close
        </Button>
        {preview && !importResult && (
          <>
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => handleImport(false)}
              disabled={importing || preview.newCount === 0}
            >
              {importing ? (
                <Spinner animation="border" size="sm" className="me-1" />
              ) : (
                <Upload size={13} className="me-1" />
              )}
              Import
              {preview.newCount > 0 ? ` (${preview.newCount})` : ""}
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={() => handleImport(true)}
              disabled={importing || preview.newCount === 0}
            >
              {importing ? (
                <Spinner animation="border" size="sm" className="me-1" />
              ) : (
                <Upload size={13} className="me-1" />
              )}
              Import &amp; run
              {preview.newCount > 0 ? ` (${preview.newCount})` : ""}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

// ── Add Domain Modal ─────────────────────────────────────

interface BulkResult {
  domain: string;
  countryCode: string;
  status: "added" | "exists" | "error";
  message?: string;
}

const AddDomainModal: React.FC<{
  show: boolean;
  onClose: () => void;
  onAdded: () => void;
}> = ({ show, onClose, onAdded }) => {
  const [tab, setTab] = useState<"single" | "bulk">("single");

  // ── Single tab state ──
  const [domain, setDomain] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [searchDone, setSearchDone] = useState(false);
  const [existingItems, setExistingItems] = useState<
    IFullAutoCouponsTargetingTrackedItem[]
  >([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addResult, setAddResult] =
    useState<IFullAutoCouponsTargetingAddItemResponse | null>(null);
  const [addError, setAddError] = useState<string | null>(null);

  // ── Bulk tab state ──
  const [bulkItems, setBulkItems] = useState<DomainGeoItem[]>([]);
  const [bulkAdding, setBulkAdding] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);
  const [bulkResults, setBulkResults] = useState<BulkResult[] | null>(null);

  const { execute: addItem } =
    usePost<IFullAutoCouponsTargetingAddItemResponse>();

  const handleSearch = async () => {
    if (!domain.trim()) return;
    setSearching(true);
    setSearchDone(false);
    setAddError(null);
    setAddResult(null);
    try {
      const params = new URLSearchParams({ domain: domain.trim() });
      if (countryCode.trim()) params.set("countryCode", countryCode.trim());
      const res = await fetch(
        `/api/full-auto-coupons-targeting/items?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") ?? ""}`,
          },
        }
      );
      const json: IFullAutoCouponsTargetingItemsResponse = await res.json();
      setExistingItems(json.items ?? []);
    } catch {
      setExistingItems([]);
    } finally {
      setSearching(false);
      setSearchDone(true);
    }
  };

  const handleAdd = async () => {
    if (!domain.trim()) return;
    setAdding(true);
    setAddError(null);
    try {
      const result = await addItem("full-auto-coupons-targeting/items", {
        domain: domain.trim(),
        countryCode: countryCode.trim().toUpperCase() || "UK",
      });
      setAddResult(result);
      onAdded();
    } catch (e: any) {
      setAddError(e?.data?.error ?? "Failed to add item.");
    } finally {
      setAdding(false);
    }
  };

  const handleBulkAdd = async () => {
    if (bulkItems.length === 0) return;
    setBulkAdding(true);
    setBulkProgress(0);
    setBulkResults(null);
    const results: BulkResult[] = [];
    for (let i = 0; i < bulkItems.length; i++) {
      const item = bulkItems[i];
      try {
        const result = await addItem("full-auto-coupons-targeting/items", {
          domain: item.domain,
          countryCode: item.countryCode,
        });
        results.push({
          domain: item.domain,
          countryCode: item.countryCode,
          status: result.created ? "added" : "exists",
        });
      } catch (e: any) {
        results.push({
          domain: item.domain,
          countryCode: item.countryCode,
          status: "error",
          message: e?.data?.error ?? "Failed",
        });
      }
      setBulkProgress(i + 1);
    }
    setBulkResults(results);
    setBulkAdding(false);
    if (results.some((r) => r.status === "added")) onAdded();
  };

  const handleClose = () => {
    setTab("single");
    setDomain("");
    setCountryCode("");
    setSearchDone(false);
    setExistingItems([]);
    setAddResult(null);
    setAddError(null);
    setBulkItems([]);
    setBulkResults(null);
    setBulkProgress(0);
    onClose();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">Add Domain to Tracking</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Nav
          variant="tabs"
          activeKey={tab}
          onSelect={(k) => setTab((k ?? "single") as "single" | "bulk")}
          className="mb-3"
        >
          <Nav.Item>
            <Nav.Link eventKey="single">Single</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="bulk">
              Bulk
              {bulkItems.length > 0 && (
                <Badge bg="primary" className="ms-1">
                  {bulkItems.length}
                </Badge>
              )}
            </Nav.Link>
          </Nav.Item>
        </Nav>

        {tab === "single" && (
          <>
            <p className="small text-muted mb-3">
              Adds a domain/country pair directly to the full-auto-coupons
              tracking list.
            </p>
            <Row className="g-2 align-items-end mb-3">
              <Col md={6}>
                <Form.Label className="small mb-0">Domain</Form.Label>
                <Form.Control
                  size="sm"
                  placeholder="e.g. example.com"
                  value={domain}
                  onChange={(e) => {
                    setDomain(e.target.value);
                    setSearchDone(false);
                    setAddResult(null);
                  }}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                />
              </Col>
              <Col md={3}>
                <Form.Label className="small mb-0">Country</Form.Label>
                <Form.Control
                  size="sm"
                  placeholder="UK"
                  value={countryCode}
                  maxLength={2}
                  onChange={(e) => {
                    setCountryCode(e.target.value.toUpperCase());
                    setSearchDone(false);
                  }}
                />
              </Col>
              <Col md={3}>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={handleSearch}
                  disabled={!domain.trim() || searching}
                  className="w-100"
                >
                  {searching ? (
                    <Spinner animation="border" size="sm" className="me-1" />
                  ) : (
                    <Search size={13} className="me-1" />
                  )}
                  Check
                </Button>
              </Col>
            </Row>

            {searchDone && existingItems.length > 0 && (
              <Alert variant="warning" className="small py-2 mb-2">
                <AlertTriangle size={12} className="me-1" />
                Already tracked ({existingItems.length} match
                {existingItems.length > 1 ? "es" : ""}):
                <ul className="mb-0 mt-1">
                  {existingItems.slice(0, 5).map((i) => (
                    <li key={i.itemId}>
                      <strong>{i.domain}</strong> / {i.countryCode}
                      {i.addedBy && (
                        <span className="text-muted"> — by {i.addedBy}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </Alert>
            )}

            {searchDone && existingItems.length === 0 && !addResult && (
              <Alert variant="info" className="small py-2 mb-2">
                <CheckCircle size={12} className="me-1" />
                Not currently tracked. Click <strong>Add</strong> to start
                monitoring.
              </Alert>
            )}

            {addResult && (
              <Alert variant="success" className="small py-2 mb-0">
                <CheckCircle size={12} className="me-1" />
                {addResult.created
                  ? "Added successfully."
                  : "Already exists — re-enabled."}
              </Alert>
            )}

            {addError && (
              <Alert variant="danger" className="small py-2 mb-0">
                {addError}
              </Alert>
            )}
          </>
        )}

        {tab === "bulk" && (
          <>
            <p className="small text-muted mb-3">
              Add multiple domain/country pairs at once. Upload a CSV, paste
              data, or add rows manually.
            </p>
            <DomainGeoPicker selected={bulkItems} onChange={setBulkItems} />

            {bulkAdding && (
              <div className="mt-3 small text-muted">
                <Spinner animation="border" size="sm" className="me-1" />
                Adding {bulkProgress} / {bulkItems.length}…
              </div>
            )}

            {bulkResults && (
              <div
                className="mt-3"
                style={{ maxHeight: 200, overflow: "auto" }}
              >
                <Table size="sm" className="mb-0 small">
                  <thead>
                    <tr>
                      <th>Domain</th>
                      <th>Country</th>
                      <th>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkResults.map((r, i) => (
                      <tr key={i}>
                        <td>{r.domain}</td>
                        <td>{r.countryCode}</td>
                        <td>
                          {r.status === "added" && (
                            <Badge bg="success">Added</Badge>
                          )}
                          {r.status === "exists" && (
                            <Badge bg="secondary">Already exists</Badge>
                          )}
                          {r.status === "error" && (
                            <Badge bg="danger" title={r.message}>
                              Error
                            </Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="py-2">
        <Button size="sm" variant="secondary" onClick={handleClose}>
          Close
        </Button>
        {tab === "single" && (
          <Button
            size="sm"
            variant="primary"
            onClick={handleAdd}
            disabled={!domain.trim() || adding || !!addResult}
          >
            {adding ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : (
              <PlusCircle size={13} className="me-1" />
            )}
            Add
          </Button>
        )}
        {tab === "bulk" && (
          <Button
            size="sm"
            variant="primary"
            onClick={handleBulkAdd}
            disabled={bulkItems.length === 0 || bulkAdding || !!bulkResults}
          >
            {bulkAdding ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : (
              <PlusCircle size={13} className="me-1" />
            )}
            Add {bulkItems.length > 0 ? `${bulkItems.length} items` : "all"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

// ── SDK Test Modal ───────────────────────────────────────

const sdkTestBadge = (item: IFullAutoCouponsTargetingTrackedItem) => {
  const { lastSdkTest: test, sdkTestStatus } = item;
  if (!test) return <span className="text-muted small">—</span>;
  const ageMs = Date.now() - new Date(test.createdAt).getTime();
  const ageDays = Math.floor(ageMs / (1000 * 60 * 60 * 24));
  const ageSuffix = ageDays === 0 ? " (today)" : ` (${ageDays}d ago)`;
  if (sdkTestStatus === "green") {
    return (
      <Badge bg="success" title={test.reason}>
        <CheckCircle size={10} className="me-1" />
        Pass{ageSuffix}
      </Badge>
    );
  }
  return (
    <Badge
      bg={test.result === "fail" ? "danger" : "warning"}
      text={test.result === "fail" ? undefined : "dark"}
      title={test.reason}
    >
      <XCircle size={10} className="me-1" />
      {test.result === "pass" ? "Pass (stale)" : "Fail"}
      {ageSuffix}
    </Badge>
  );
};

/** Two small pills showing the checkout 1 / checkout 2 timing buckets from the
 *  SDK test persisted against a snapshot. Coloured by each checkout's result. */
const sdkCheckoutPills = (metrics: any) => {
  const test = metrics?.lastSdkTest;
  if (!test) return null;
  const pill = (label: string, result: "pass" | "fail", timing: string) => (
    <Badge
      bg={result === "pass" ? "success" : "danger"}
      title={`${label}: ${result === "pass" ? "pass" : "fail"} (${sdkTestCheckoutTimingLabel(
        timing
      )})`}
      style={{ fontSize: 9 }}
    >
      {sdkTestCheckoutTimingLabel(timing)}
    </Badge>
  );
  return (
    <div className="d-flex gap-1">
      {pill("Checkout 1", test.firstCheckoutResult, test.firstCheckoutTiming)}
      {pill("Checkout 2", test.secondCheckoutResult, test.secondCheckoutTiming)}
    </div>
  );
};

const booleanFilterOptions = [
  { value: "", label: "All" },
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

const fmtPct = (n: number) => `${Math.round(n * 100)}%`;

const yesNoBadge = (value: boolean) =>
  value ? (
    <Badge bg="success">
      <CheckCircle size={10} /> Yes
    </Badge>
  ) : (
    <Badge bg="danger">
      <XCircle size={10} /> No
    </Badge>
  );

const statusBadge = (status: FullAutoCouponsTargetingCheckStatus) => {
  if (status === "green")
    return (
      <Badge bg="success">
        <CheckCircle size={10} /> OK
      </Badge>
    );
  if (status === "amber")
    return (
      <Badge bg="warning" text="dark">
        <AlertTriangle size={10} /> Pending
      </Badge>
    );
  if (status === "error")
    return (
      <Badge bg="secondary">
        <AlertTriangle size={10} /> Error
      </Badge>
    );
  return (
    <Badge bg="danger">
      <XCircle size={10} /> Missing
    </Badge>
  );
};

export const FullAutoCouponsTargeting: React.FC = () => {
  const { can } = usePermissions();

  const canManageTargeting =
    can(
      PermissionAction.CreateAny,
      PermissionResource.FullAutoCouponsTargeting
    ) ||
    can(
      PermissionAction.UpdateAny,
      PermissionResource.FullAutoCouponsTargeting
    ) ||
    can(
      PermissionAction.DeleteAny,
      PermissionResource.FullAutoCouponsTargeting
    );

  const [date, setDate] = useState<string>("");
  const [search, setSearch] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [overallStatus, setOverallStatus] = useState("");
  const [hasDeal, setHasDeal] = useState("");
  const [hasAutoTemplate, setHasAutoTemplate] = useState("");
  const [hasSkuTemplate, setHasSkuTemplate] = useState("");
  const [hasAnyOffer, setHasAnyOffer] = useState("");
  const [hasAnySitewideCoupon, setHasAnySitewideCoupon] = useState("");
  const [noActiveCoupons, setNoActiveCoupons] = useState("");
  const [metadataCoverage, setMetadataCoverage] = useState("");
  const [targetingCoverage, setTargetingCoverage] = useState("");
  const [hasCoverageMismatch, setHasCoverageMismatch] = useState("");
  const [sdkTestPassed, setSdkTestPassed] = useState("");

  // ── New state for item management ──
  const [view, setView] = useState<
    "snapshots" | "items" | "runs" | "reporting"
  >("snapshots");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [runningItems, setRunningItems] = useState<Set<string>>(new Set());
  const [removingItems, setRemovingItems] = useState<Set<string>>(new Set());
  const [resetting, setResetting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sdkTestItem, setSdkTestItem] =
    useState<IFullAutoCouponsTargetingTrackedItem | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  // ── Snapshots pagination ──
  const [snapshotsPage, setSnapshotsPage] = useState(1);
  const SNAPSHOTS_PAGE_SIZE = 200;

  // ── Tracked items filter / pagination ──
  // Draft values bound to the inputs; committed only on Search click / Enter.
  const [itemsSearchDraft, setItemsSearchDraft] = useState("");
  const [itemsCountryDraft, setItemsCountryDraft] = useState("");
  // Applied values that drive the API query.
  const [itemsSearch, setItemsSearch] = useState("");
  const [itemsCountry, setItemsCountry] = useState("");
  const [itemsPage, setItemsPage] = useState(1);
  const ITEMS_PAGE_SIZE = 200;

  // ── Runs filter / pagination ──
  const [runsStatus, setRunsStatus] = useState("");
  const [runsType, setRunsType] = useState("");
  const [runsTriggeredBy, setRunsTriggeredBy] = useState("");
  // "" until the server resolves the default (latest date); "all" = every date;
  // otherwise a specific YYYY-MM-DD.
  const [runsDate, setRunsDate] = useState("");
  const [runsPage, setRunsPage] = useState(1);
  const RUNS_PAGE_SIZE = 20;
  const [cancellingRunId, setCancellingRunId] = useState<string | null>(null);

  // Builds the snapshots URL from current filter state (no page — appended by withPage)
  const buildSnapshotsUrl = () => {
    const params = new URLSearchParams();
    if (date) params.set("date", date);
    if (search) params.set("search", search);
    if (countryCode) params.set("countryCode", countryCode);
    if (overallStatus) params.set("overallStatus", overallStatus);
    if (hasDeal) params.set("hasDeal", hasDeal);
    if (hasAutoTemplate) params.set("hasAutoTemplate", hasAutoTemplate);
    if (hasSkuTemplate) params.set("hasSkuTemplate", hasSkuTemplate);
    if (hasAnyOffer) params.set("hasAnyOffer", hasAnyOffer);
    if (hasAnySitewideCoupon)
      params.set("hasAnySitewideCoupon", hasAnySitewideCoupon);
    if (noActiveCoupons) params.set("noActiveCoupons", noActiveCoupons);
    if (metadataCoverage) params.set("metadataCoverage", metadataCoverage);
    if (targetingCoverage) params.set("targetingCoverage", targetingCoverage);
    if (hasCoverageMismatch)
      params.set("hasCoverageMismatch", hasCoverageMismatch);
    if (sdkTestPassed) params.set("sdkTestPassed", sdkTestPassed);
    const qs = params.toString();
    return `full-auto-coupons-targeting/snapshots${qs ? `?${qs}` : ""}`;
  };

  const withPage = (base: string, page: number) => {
    const sep = base.includes("?") ? "&" : "?";
    return `${base}${sep}page=${page}&pageSize=${SNAPSHOTS_PAGE_SIZE}`;
  };

  const applySnapshotFilter = async () => {
    setSnapshotsPage(1);
    const result = await fetchSnapshots(withPage(buildSnapshotsUrl(), 1));
    if (result) setSnapshotsData(result);
  };

  const goToSnapshotPage = async (page: number) => {
    setSnapshotsPage(page);
    const result = await fetchSnapshots(withPage(buildSnapshotsUrl(), page));
    if (result) setSnapshotsData(result);
  };

  const applyItemsFilter = async () => {
    const newSearch = itemsSearchDraft;
    const newCountry = itemsCountryDraft;
    setItemsSearch(newSearch);
    setItemsCountry(newCountry);
    setItemsPage(1);
    const params = new URLSearchParams();
    params.set("page", "1");
    params.set("pageSize", String(ITEMS_PAGE_SIZE));
    if (newSearch) params.set("domain", newSearch);
    if (newCountry) params.set("countryCode", newCountry);
    const r = await fetchItems(
      `full-auto-coupons-targeting/items?${params.toString()}`
    );
    if (r) setItemsData(r);
  };

  const clearItemsFilter = async () => {
    setItemsSearchDraft("");
    setItemsCountryDraft("");
    setItemsSearch("");
    setItemsCountry("");
    setItemsPage(1);
    const r = await fetchItems(
      `full-auto-coupons-targeting/items?page=1&pageSize=${ITEMS_PAGE_SIZE}`
    );
    if (r) setItemsData(r);
  };

  const goToItemsPage = async (page: number) => {
    setItemsPage(page);
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("pageSize", String(ITEMS_PAGE_SIZE));
    if (itemsSearch) params.set("domain", itemsSearch);
    if (itemsCountry) params.set("countryCode", itemsCountry);
    const r = await fetchItems(
      `full-auto-coupons-targeting/items?${params.toString()}`
    );
    if (r) setItemsData(r);
  };

  const { data: datesResp } = useGet<DatesResponse>(
    "full-auto-coupons-targeting/dates"
  );

  // queryUrl (no page) — used for CSV export which should export all matching results
  const queryUrl = useMemo(
    () => buildSnapshotsUrl(),
    [
      date,
      search,
      countryCode,
      overallStatus,
      hasDeal,
      hasAutoTemplate,
      hasSkuTemplate,
      hasAnyOffer,
      hasAnySitewideCoupon,
      noActiveCoupons,
      metadataCoverage,
      targetingCoverage,
      hasCoverageMismatch,
      sdkTestPassed,
    ]
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const [snapshotsData, setSnapshotsData] =
    useState<IFullAutoCouponsTargetingSnapshotsResponse | null>(null);
  const {
    execute: fetchSnapshots,
    loading,
    error,
  } = useGetLazy<IFullAutoCouponsTargetingSnapshotsResponse>();

  const refetchSnapshots = async () => {
    const result = await fetchSnapshots(
      withPage(buildSnapshotsUrl(), snapshotsPage)
    );
    if (result) setSnapshotsData(result);
  };

  const [runsData, setRunsData] = useState<RunsResponse | null>(null);
  const { execute: fetchRuns, loading: runsLoading } =
    useGetLazy<RunsResponse>();
  const runsResp = runsData;

  // Active-runs list is loaded lazily (only when the section is expanded).
  // null = never loaded yet; [] = loaded and empty.
  const [activeRunsData, setActiveRunsData] = useState<
    IFullAutoCouponsTargetingRunSummary[] | null
  >(null);
  const { execute: fetchActiveRuns, loading: activeRunsLoading } =
    useGetLazy<IFullAutoCouponsTargetingActiveRunsResponse>();

  const [reportData, setReportData] =
    useState<IFullAutoCouponsTargetingDailyReportResponse | null>(null);
  const { execute: fetchReport, loading: reportLoading } =
    useGetLazy<IFullAutoCouponsTargetingDailyReportResponse>();
  const reportResp = reportData;

  const itemsQueryUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.set("page", String(itemsPage));
    params.set("pageSize", String(ITEMS_PAGE_SIZE));
    if (itemsSearch) params.set("domain", itemsSearch);
    if (itemsCountry) params.set("countryCode", itemsCountry);
    return `full-auto-coupons-targeting/items?${params.toString()}`;
  }, [itemsPage, itemsSearch, itemsCountry, ITEMS_PAGE_SIZE]);

  const [itemsData, setItemsData] =
    useState<IFullAutoCouponsTargetingItemsResponse | null>(null);
  const { execute: fetchItems, loading: itemsLoading } =
    useGetLazy<IFullAutoCouponsTargetingItemsResponse>();
  const itemsResp = itemsData;
  const trackedItems = itemsResp?.items ?? [];
  const totalItemsPages = Math.max(
    1,
    Math.ceil((itemsResp?.total ?? 0) / ITEMS_PAGE_SIZE)
  );

  // Fetches the full active-runs list on demand (when the section is expanded).
  const loadActiveRuns = async () => {
    const r = await fetchActiveRuns("full-auto-coupons-targeting/runs/active");
    if (r) setActiveRunsData(r.activeRuns);
  };

  // Builds the runs URL from current filter state. `dateOverride` lets callers
  // force a value (e.g. "" to fall back to the server's latest-date default).
  const buildRunsUrl = (page: number, dateOverride?: string) => {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("pageSize", String(RUNS_PAGE_SIZE));
    if (runsStatus) params.set("status", runsStatus);
    if (runsType) params.set("type", runsType);
    if (runsTriggeredBy) params.set("triggeredBy", runsTriggeredBy);
    const d = dateOverride !== undefined ? dateOverride : runsDate;
    if (d) params.set("snapshotDate", d);
    return `full-auto-coupons-targeting/runs?${params.toString()}`;
  };

  const refetchRuns = async () => {
    const r = await fetchRuns(buildRunsUrl(runsPage));
    if (r) {
      setRunsData(r);
      // Reflect the server-resolved default date in the picker on first load.
      if (!runsDate && r.snapshotDate) setRunsDate(r.snapshotDate);
    }
    // If the active-runs section has been opened, keep it in sync too.
    if (activeRunsData !== null) loadActiveRuns();
  };

  const applyRunsFilter = async () => {
    setRunsPage(1);
    const r = await fetchRuns(buildRunsUrl(1));
    if (r) {
      setRunsData(r);
      if (!runsDate && r.snapshotDate) setRunsDate(r.snapshotDate);
    }
  };

  const clearRunsFilter = async () => {
    setRunsStatus("");
    setRunsType("");
    setRunsTriggeredBy("");
    setRunsDate("");
    setRunsPage(1);
    // "" override → server defaults to the latest date again.
    const r = await fetchRuns(buildRunsUrl(1, ""));
    if (r) {
      setRunsData(r);
      if (r.snapshotDate) setRunsDate(r.snapshotDate);
    }
  };

  const goToRunsPage = async (page: number) => {
    setRunsPage(page);
    const r = await fetchRuns(buildRunsUrl(page));
    if (r) setRunsData(r);
  };

  const refetchReport = async () => {
    const r = await fetchReport("full-auto-coupons-targeting/report/daily");
    if (r) setReportData(r);
  };

  const refetchItems = async () => {
    const r = await fetchItems(itemsQueryUrl);
    if (r) setItemsData(r);
  };

  // Load data when switching tabs.
  // The ref guard prevents the double-invocation caused by React 18 StrictMode
  // (effects are cleaned up and re-run on mount; refs persist across that cycle).
  const lastFetchedViewRef = useRef<string | null>(null);
  useEffect(() => {
    if (lastFetchedViewRef.current === view) return;
    lastFetchedViewRef.current = view;
    if (view === "snapshots") {
      fetchSnapshots(
        `full-auto-coupons-targeting/snapshots?page=1&pageSize=${SNAPSHOTS_PAGE_SIZE}`
      ).then((r) => {
        if (r) setSnapshotsData(r);
      });
    } else if (view === "runs") {
      fetchRuns(buildRunsUrl(1)).then((r) => {
        if (r) {
          setRunsData(r);
          if (!runsDate && r.snapshotDate) setRunsDate(r.snapshotDate);
        }
      });
    } else if (view === "reporting") {
      fetchReport("full-auto-coupons-targeting/report/daily").then((r) => {
        if (r) setReportData(r);
      });
    } else if (view === "items") {
      fetchItems(itemsQueryUrl).then((r) => {
        if (r) setItemsData(r);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const { execute: triggerRun, loading: triggering } = usePost<{
    status: string;
  }>();

  const { execute: triggerMissingRun, loading: triggeringMissing } = usePost<{
    status: string;
  }>();

  const {
    execute: refreshTemplateCountsApi,
    loading: refreshingTemplateCounts,
  } = usePost<{
    totalAutoTemplates: number | null;
    totalSkuTemplates: number | null;
  }>();

  const { execute: cancelRunApi, loading: cancelling } = useDelete<{
    cancelled: boolean;
    runId: string;
  }>();

  const { execute: fetchRunDetail, loading: runDetailLoading } =
    useGetLazy<IFullAutoCouponsTargetingRunDetail>();
  const [selectedRunDetail, setSelectedRunDetail] =
    useState<IFullAutoCouponsTargetingRunDetail | null>(null);

  const handleViewRunDetail = async (runId: string) => {
    const detail = await fetchRunDetail(
      `full-auto-coupons-targeting/runs/${runId}`
    );
    if (detail) setSelectedRunDetail(detail);
  };

  const { execute: runItemApi } = usePost<{ status: string }>();
  const { execute: removeItemApi } = useDelete<{ success: boolean }>();
  const { execute: resetItemsApi } =
    usePost<IFullAutoCouponsTargetingResetResponse>();
  const { execute: fetchCsv } = useGetLazy<string>();

  const handleTriggerRun = async () => {
    if (!canManageTargeting) return;
    setRunError(null);
    try {
      await triggerRun("full-auto-coupons-targeting/runs");
      setTimeout(() => {
        refetchRuns();
      }, 500);
    } catch (err: any) {
      if (err?.status === 409) {
        setRunError(
          err?.data?.error ??
            "A run is already in progress. Wait for it to finish or cancel it first."
        );
        refetchRuns();
      }
    }
  };

  const handleCancelRun = async () => {
    try {
      await cancelRunApi("full-auto-coupons-targeting/runs/active");
      setRunError(null);
    } catch {
      // 404 means run already finished/cancelled — still refresh to sync UI
    } finally {
      refetchRuns();
    }
  };

  // Cancel any specific run (bulk or single, incl. old stuck ones) by id.
  const handleCancelRunById = async (runId: string) => {
    if (!canManageTargeting) return;
    setCancellingRunId(runId);
    try {
      await cancelRunApi(`full-auto-coupons-targeting/runs/${runId}`);
    } catch {
      // 404 = run already finished/cancelled; the refetch below reconciles it.
    } finally {
      setCancellingRunId(null);
      refetchRuns();
    }
  };

  const handleTriggerMissingRun = async () => {
    if (!canManageTargeting) return;
    setRunError(null);
    try {
      await triggerMissingRun("full-auto-coupons-targeting/runs/missing");
      setTimeout(() => {
        refetchRuns();
      }, 500);
    } catch (err: any) {
      if (err?.status === 409) {
        setRunError(
          err?.data?.error ??
            "A run is already in progress. Wait for it to finish or cancel it first."
        );
        refetchRuns();
      }
    }
  };

  const handleRefreshTemplateCounts = async () => {
    if (!canManageTargeting) return;
    setRunError(null);
    try {
      await refreshTemplateCountsApi(
        "full-auto-coupons-targeting/template-counts/refresh"
      );
      refetchReport();
    } catch {
      setRunError("Failed to refresh template counts.");
    }
  };

  const handleRunItem = async (itemId: string) => {
    setRunningItems((prev) => new Set(prev).add(itemId));
    try {
      await runItemApi("full-auto-coupons-targeting/runs/item", {
        qaItemId: itemId,
      });
    } catch {
      // fire-and-forget 202; errors are logged server-side
    } finally {
      setRunningItems((prev) => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
    // Backend acknowledged — switch to snapshots and refresh.
    // The run itself is async on the server; give it a moment then reload.
    refetchSnapshots();
  };

  const handleRemoveItem = async (itemId: string) => {
    if (!canManageTargeting) return;
    setRemovingItems((prev) => new Set(prev).add(itemId));
    try {
      await removeItemApi(`full-auto-coupons-targeting/items/${itemId}`);
      refetchSnapshots();
      refetchItems();
    } finally {
      setRemovingItems((prev) => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  };

  const handleResetAll = async () => {
    if (!canManageTargeting) return;
    setResetting(true);
    try {
      await resetItemsApi("full-auto-coupons-targeting/items/reset", {});
      setShowResetConfirm(false);
      refetchSnapshots();
      refetchItems();
      refetchRuns();
    } finally {
      setResetting(false);
    }
  };

  const handleDownloadCsv = async () => {
    if (!canManageTargeting) return;
    setDownloading(true);
    try {
      const csvUrl = queryUrl.replace(
        "full-auto-coupons-targeting/snapshots",
        "full-auto-coupons-targeting/snapshots/csv"
      );
      const csvText = await fetchCsv(csvUrl, { responseType: "text" });
      if (!csvText) return;
      const blob = new Blob([csvText], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const dateSegment = queryUrl.match(/date=([^&]+)/)?.[1] ?? "snapshot";
      a.download = `full-auto-coupons-targeting-${dateSegment}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
    setCountryCode("");
    setDate("");
    setSnapshotsPage(1);
    setOverallStatus("");
    setHasDeal("");
    setHasAutoTemplate("");
    setHasSkuTemplate("");
    setHasAnyOffer("");
    setHasAnySitewideCoupon("");
    setNoActiveCoupons("");
    setMetadataCoverage("");
    setTargetingCoverage("");
    setHasCoverageMismatch("");
    setSdkTestPassed("");
    fetchSnapshots(
      `full-auto-coupons-targeting/snapshots?page=1&pageSize=${SNAPSHOTS_PAGE_SIZE}`
    ).then((r) => {
      if (r) setSnapshotsData(r);
    });
  };

  const totals = snapshotsData?.totals;
  const rows = snapshotsData?.results ?? [];
  const totalSnapshotsPages = Math.max(
    1,
    Math.ceil((snapshotsData?.total ?? 0) / SNAPSHOTS_PAGE_SIZE)
  );

  // Cheap counts from the list response drive the badge and the toolbar guard;
  // the active-runs list itself is loaded lazily when the section is expanded.
  const activeCount = runsResp?.activeCount ?? 0;
  const latestRun = runsResp?.runs?.[0];
  const isRunning = runsResp?.activeBulkRunning ?? false;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Full Auto Coupons Targeting</h4>
        <div className="d-flex gap-2">
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => {
              if (view === "snapshots") refetchSnapshots();
              else if (view === "runs") refetchRuns();
              else if (view === "items") refetchItems();
              else if (view === "reporting") refetchReport();
            }}
          >
            <RefreshCw size={14} className="me-1" />
            Refresh
          </Button>
          <Button
            size="sm"
            variant="outline-info"
            onClick={() => setShowImportModal(true)}
            disabled={!canManageTargeting}
          >
            <Upload size={14} className="me-1" />
            Import live items
          </Button>
          <Button
            size="sm"
            variant="outline-success"
            onClick={() => {
              setShowAddModal(true);
            }}
          >
            <PlusCircle size={14} className="me-1" />
            Add domain
          </Button>
          <Button
            size="sm"
            variant="outline-danger"
            onClick={() => setShowResetConfirm(true)}
            disabled={!canManageTargeting}
          >
            <Trash2 size={14} className="me-1" />
            Reset all
          </Button>
          <Button
            size="sm"
            variant="outline-primary"
            onClick={handleDownloadCsv}
            disabled={downloading || !canManageTargeting}
          >
            {downloading ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : (
              <Download size={14} className="me-1" />
            )}
            Download CSV
          </Button>
          <Dropdown as={ButtonGroup} size="sm">
            <Button
              variant="primary"
              onClick={handleTriggerRun}
              disabled={
                !canManageTargeting ||
                triggering ||
                triggeringMissing ||
                refreshingTemplateCounts ||
                isRunning
              }
            >
              {triggering ? (
                <Spinner animation="border" size="sm" className="me-1" />
              ) : (
                <Play size={14} className="me-1" />
              )}
              Run now
            </Button>
            <Dropdown.Toggle
              split
              variant="primary"
              disabled={
                !canManageTargeting ||
                triggering ||
                triggeringMissing ||
                refreshingTemplateCounts ||
                isRunning
              }
            ></Dropdown.Toggle>
            <Dropdown.Menu align="end">
              <Dropdown.Item
                onClick={handleTriggerMissingRun}
                disabled={!canManageTargeting || triggeringMissing || isRunning}
                title="Run only items that don't have a snapshot for today — use this to resume after a failed/partial run"
              >
                {triggeringMissing ? (
                  <Spinner animation="border" size="sm" className="me-2" />
                ) : (
                  <Play size={13} className="me-2" />
                )}
                Run missing
              </Dropdown.Item>
              <Dropdown.Item
                onClick={handleRefreshTemplateCounts}
                disabled={!canManageTargeting || refreshingTemplateCounts}
                title="Fetch and save the current total template counts from SQL"
              >
                {refreshingTemplateCounts ? (
                  <Spinner animation="border" size="sm" className="me-2" />
                ) : (
                  <Database size={13} className="me-2" />
                )}
                Refresh total templates
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          {isRunning && (
            <Button
              size="sm"
              variant="outline-danger"
              onClick={handleCancelRun}
              disabled={cancelling || !canManageTargeting}
            >
              {cancelling ? (
                <Spinner animation="border" size="sm" className="me-1" />
              ) : (
                <XCircle size={14} className="me-1" />
              )}
              Cancel run
            </Button>
          )}
        </div>
      </div>

      {runError && (
        <Alert
          variant="warning"
          dismissible
          onClose={() => setRunError(null)}
          className="mb-3 py-2"
        >
          {runError}
        </Alert>
      )}

      {/* Import live items modal */}
      <ImportLiveItemsModal
        show={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImported={() => {
          refetchItems();
          refetchSnapshots();
          setView("items");
        }}
      />

      {/* Add domain modal */}
      <AddDomainModal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdded={() => {
          refetchItems();
          setView("items");
        }}
      />

      {/* Reset confirmation modal */}
      <Modal
        show={showResetConfirm}
        onHide={() => setShowResetConfirm(false)}
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">Reset all tracked items</Modal.Title>
        </Modal.Header>
        <Modal.Body className="small">
          This will remove ALL items from full-auto-coupons tracking. This
          action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setShowResetConfirm(false)}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={handleResetAll}
            disabled={resetting}
          >
            {resetting ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : (
              <Trash2 size={13} className="me-1" />
            )}
            Reset all
          </Button>
        </Modal.Footer>
      </Modal>

      {/* SDK test modal */}
      <SDKTestModal
        show={!!sdkTestItem}
        itemId={sdkTestItem?.itemId ?? ""}
        domain={sdkTestItem?.domain ?? ""}
        countryCode={sdkTestItem?.countryCode ?? ""}
        onClose={() => setSdkTestItem(null)}
        onSaved={() => {
          refetchItems();
        }}
      />

      {/* Tab switcher */}
      <Nav
        variant="tabs"
        activeKey={view}
        onSelect={(k) =>
          setView(
            (k ?? "snapshots") as "snapshots" | "items" | "runs" | "reporting"
          )
        }
        className="mb-3"
      >
        <Nav.Item>
          <Nav.Link eventKey="snapshots">
            Snapshots
            {snapshotsData && (
              <Badge bg="secondary" className="ms-2">
                {snapshotsData.total ?? snapshotsData.results.length}
              </Badge>
            )}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="items">
            Tracked items
            <Badge
              bg={(itemsResp?.total ?? 0) > 0 ? "primary" : "secondary"}
              className="ms-2"
            >
              {itemsResp?.total ?? 0}
            </Badge>
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="runs">
            Runs
            {runsResp && (
              <Badge bg="secondary" className="ms-2">
                {runsResp.total}
              </Badge>
            )}
            {activeCount > 0 && (
              <Badge
                bg="primary"
                className="ms-1"
                title="Runs currently pending or running"
              >
                {activeCount} active
              </Badge>
            )}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="reporting">
            Reporting
            {reportResp && (
              <Badge bg="info" className="ms-2">
                {reportResp.entries.length}d
              </Badge>
            )}
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {/* ── TRACKED ITEMS VIEW ── */}
      {view === "items" && (
        <>
          <Alert variant="info" className="py-2 small">
            These are all domains being tracked. Items only appear in the
            Snapshots tab after their first run. Use the <strong>▶</strong>{" "}
            button to run one now, or <strong>Run now</strong> (top right) to
            run all.
          </Alert>

          {/* Tracked items filters */}
          <Card className="mb-3">
            <Card.Body className="py-2">
              <Row className="g-2 align-items-end">
                <Col md={4}>
                  <Form.Label className="small mb-0">Search domain</Form.Label>
                  <Form.Control
                    size="sm"
                    placeholder="e.g. example.com"
                    value={itemsSearchDraft}
                    onChange={(e) => setItemsSearchDraft(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && applyItemsFilter()}
                  />
                </Col>
                <Col md={2}>
                  <Form.Label className="small mb-0">Country</Form.Label>
                  <Form.Control
                    size="sm"
                    placeholder="UK"
                    maxLength={2}
                    value={itemsCountryDraft}
                    onChange={(e) =>
                      setItemsCountryDraft(
                        e.target.value.toUpperCase().slice(0, 2)
                      )
                    }
                    onKeyDown={(e) => e.key === "Enter" && applyItemsFilter()}
                  />
                </Col>
                <Col md={2}>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={applyItemsFilter}
                  >
                    <Search size={13} className="me-1" />
                    Search
                  </Button>
                </Col>
                <Col md={2}>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={clearItemsFilter}
                  >
                    Clear
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {itemsLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : !itemsLoading &&
            (itemsResp?.total ?? 0) === 0 &&
            !itemsSearch &&
            !itemsCountry ? (
            <Alert variant="secondary" className="small">
              No items tracked yet. Use <strong>Add domain</strong> to start
              monitoring a domain.
            </Alert>
          ) : (
            <>
              <Card>
                <Card.Body className="p-0">
                  <Table size="sm" hover className="mb-0 small">
                    <thead className="table-light">
                      <tr>
                        <th>Domain</th>
                        <th>Country</th>
                        <th>Added by</th>
                        <th>Added at</th>
                        <th>SDK Test</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trackedItems.length === 0 ? (
                        <tr>
                          <td
                            colSpan={6}
                            className="text-center text-muted py-3"
                          >
                            No items match the current filters.
                          </td>
                        </tr>
                      ) : (
                        trackedItems.map((item) => (
                          <tr key={item.itemId}>
                            <td>
                              <a
                                href={`https://${item.domain}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {item.domain}
                              </a>
                            </td>
                            <td>{item.countryCode}</td>
                            <td className="text-muted">
                              {item.addedBy ?? "—"}
                            </td>
                            <td className="text-muted">
                              {new Date(item.createdAt).toLocaleDateString()}
                            </td>
                            <td>{sdkTestBadge(item)}</td>
                            <td>
                              <div className="d-flex gap-1 align-items-center flex-nowrap">
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  className="py-0 px-1"
                                  style={{ fontSize: 11 }}
                                  title="Run now — computes a fresh snapshot"
                                  disabled={runningItems.has(item.itemId)}
                                  onClick={() => handleRunItem(item.itemId)}
                                >
                                  {runningItems.has(item.itemId) ? (
                                    <Spinner animation="border" size="sm" />
                                  ) : (
                                    <>
                                      <Play size={11} className="me-1" />
                                      Run
                                    </>
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  className="py-0 px-1"
                                  style={{ fontSize: 11 }}
                                  title="Record SDK test"
                                  onClick={() => setSdkTestItem(item)}
                                >
                                  <Activity size={11} className="me-1" />
                                  SDK Test
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  className="py-0 px-1"
                                  style={{ fontSize: 11 }}
                                  title="Remove from tracking"
                                  disabled={
                                    removingItems.has(item.itemId) ||
                                    !canManageTargeting
                                  }
                                  onClick={() => handleRemoveItem(item.itemId)}
                                >
                                  {removingItems.has(item.itemId) ? (
                                    <Spinner animation="border" size="sm" />
                                  ) : (
                                    <Trash2 size={11} />
                                  )}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>

              {/* Pagination */}
              {totalItemsPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-2 small text-muted">
                  <span>
                    Showing{" "}
                    {Math.min(
                      (itemsPage - 1) * ITEMS_PAGE_SIZE + 1,
                      itemsResp?.total ?? 0
                    )}
                    –
                    {Math.min(
                      itemsPage * ITEMS_PAGE_SIZE,
                      itemsResp?.total ?? 0
                    )}{" "}
                    of {itemsResp?.total ?? 0}
                  </span>
                  <div className="d-flex gap-1">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={itemsPage === 1}
                      onClick={() => goToItemsPage(1)}
                    >
                      «
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={itemsPage === 1}
                      onClick={() => goToItemsPage(itemsPage - 1)}
                    >
                      ‹
                    </Button>
                    <span className="px-2 d-flex align-items-center">
                      Page {itemsPage} / {totalItemsPages}
                    </span>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={itemsPage === totalItemsPages}
                      onClick={() => goToItemsPage(itemsPage + 1)}
                    >
                      ›
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={itemsPage === totalItemsPages}
                      onClick={() => goToItemsPage(totalItemsPages)}
                    >
                      »
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ── SNAPSHOTS VIEW ── */}
      {view === "snapshots" && (
        <>
          {/* Last run */}
          {latestRun && (
            <Alert
              variant={latestRun.status === "failed" ? "danger" : "info"}
              className="py-2 small"
            >
              <strong>Last run:</strong> {latestRun.snapshotDate} —{" "}
              <Badge bg={runStatusVariant(latestRun.status)} className="me-1">
                {latestRun.status}
              </Badge>
              {latestRun.itemsProcessed}/{latestRun.itemsConsidered} snapshots (
              {latestRun.itemsFailed} failed) · {latestRun.totalBatches} batch
              {latestRun.totalBatches !== 1 ? "es" : ""} (
              {latestRun.completedBatches} done
              {latestRun.failedBatches > 0
                ? `, ${latestRun.failedBatches} failed`
                : ""}
              )
              {latestRun.status === "running" ||
              latestRun.status === "pending" ? (
                <>, started {new Date(latestRun.startedAt).toLocaleString()}</>
              ) : latestRun.completedAt ? (
                <>
                  , finished {new Date(latestRun.completedAt).toLocaleString()}
                </>
              ) : null}
              {totals?.itemCount != null && (
                <span className="text-muted ms-1">
                  · {totals.itemCount} unique domain
                  {totals.itemCount !== 1 ? "s" : ""}
                </span>
              )}
            </Alert>
          )}

          {/* Filters */}
          <Card className="mb-3">
            <Card.Body className="py-2">
              <Row className="g-2 align-items-end">
                <Col md={2}>
                  <Form.Label className="small mb-0">Snapshot date</Form.Label>
                  <Form.Select
                    size="sm"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  >
                    <option value="">Latest</option>
                    {(datesResp?.dates ?? []).map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Form.Label className="small mb-0">Search domain</Form.Label>
                  <Form.Control
                    size="sm"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && applySnapshotFilter()
                    }
                  />
                </Col>
                <Col md={1}>
                  <Form.Label className="small mb-0">Country</Form.Label>
                  <Form.Control
                    size="sm"
                    value={countryCode}
                    onChange={(e) =>
                      setCountryCode(e.target.value.toUpperCase().slice(0, 2))
                    }
                    onKeyDown={(e) =>
                      e.key === "Enter" && applySnapshotFilter()
                    }
                  />
                </Col>
                <Col md={1}>
                  <Form.Label className="small mb-0">Status</Form.Label>
                  <Form.Select
                    size="sm"
                    value={overallStatus}
                    onChange={(e) => setOverallStatus(e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="green">Green</option>
                    <option value="amber">Amber</option>
                    <option value="red">Red</option>
                    <option value="error">Error (system)</option>
                  </Form.Select>
                </Col>
                <Col md={1}>
                  <Form.Label className="small mb-0">Has deal</Form.Label>
                  <Form.Select
                    size="sm"
                    value={hasDeal}
                    onChange={(e) => setHasDeal(e.target.value)}
                  >
                    {booleanFilterOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={1}>
                  <Form.Label className="small mb-0">Auto tpl</Form.Label>
                  <Form.Select
                    size="sm"
                    value={hasAutoTemplate}
                    onChange={(e) => setHasAutoTemplate(e.target.value)}
                  >
                    {booleanFilterOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={1}>
                  <Form.Label className="small mb-0">SKU tpl</Form.Label>
                  <Form.Select
                    size="sm"
                    value={hasSkuTemplate}
                    onChange={(e) => setHasSkuTemplate(e.target.value)}
                  >
                    {booleanFilterOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={1}>
                  <Form.Label className="small mb-0">Sitewide</Form.Label>
                  <Form.Select
                    size="sm"
                    value={hasAnySitewideCoupon}
                    onChange={(e) => setHasAnySitewideCoupon(e.target.value)}
                  >
                    {booleanFilterOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={1}>
                  <Form.Label className="small mb-0">Has offer</Form.Label>
                  <Form.Select
                    size="sm"
                    value={hasAnyOffer}
                    onChange={(e) => setHasAnyOffer(e.target.value)}
                  >
                    {booleanFilterOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={1}>
                  <Form.Label className="small mb-0">No coupons</Form.Label>
                  <Form.Select
                    size="sm"
                    value={noActiveCoupons}
                    onChange={(e) => setNoActiveCoupons(e.target.value)}
                  >
                    {booleanFilterOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>
              <Row className="g-2 align-items-end mt-1">
                <Col md={2}>
                  <Form.Label className="small mb-0">
                    Metadata coverage
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={metadataCoverage}
                    onChange={(e) => setMetadataCoverage(e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="complete">Complete</option>
                    <option value="partial">Partial (some missing)</option>
                    <option value="none">None</option>
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Form.Label className="small mb-0">
                    Targeting coverage
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={targetingCoverage}
                    onChange={(e) => setTargetingCoverage(e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="complete">Complete</option>
                    <option value="partial">Partial (some missing)</option>
                    <option value="none">None</option>
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Form.Label className="small mb-0">
                    Coverage mismatch
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={hasCoverageMismatch}
                    onChange={(e) => setHasCoverageMismatch(e.target.value)}
                  >
                    {booleanFilterOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Form.Label className="small mb-0">SDK test</Form.Label>
                  <Form.Select
                    size="sm"
                    value={sdkTestPassed}
                    onChange={(e) => setSdkTestPassed(e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="passed">Passed</option>
                    <option value="failed">Failed</option>
                    <option value="untested">Untested</option>
                  </Form.Select>
                </Col>
                <Col md={2} className="d-flex gap-2 align-items-end">
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={applySnapshotFilter}
                    disabled={loading}
                  >
                    <Search size={13} className="me-1" />
                    Apply filters
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={clearFilters}
                  >
                    Clear
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Totals */}
          {totals && (
            <Row className="g-2 mb-3">
              <SummaryTile label="Total" value={totals.itemCount} />
              <SummaryTile label="Green" value={totals.greenCount} positive />
              <SummaryTile label="Amber" value={totals.amberCount} amber />
              <SummaryTile label="Red" value={totals.redCount} negative />
              <SummaryTile label="Error" value={totals.errorCount} />
              <SummaryTile label="Has deal" value={totals.hasDealCount} />
              <SummaryTile
                label="Auto tpl"
                value={totals.hasAutoTemplateCount}
              />
              <SummaryTile label="SKU tpl" value={totals.hasSkuTemplateCount} />
              <SummaryTile
                label="Sitewide"
                value={totals.hasSitewideCouponCount}
              />
              <SummaryTile
                label="With metadata"
                value={totals.withMetadataCount}
              />
              <SummaryTile
                label="With targeting"
                value={totals.withTargetingCount}
              />
              <SummaryTile
                label="Coverage mismatch"
                value={totals.coverageMismatchCount}
                negative
              />
              <SummaryTile
                label="SDK test passed"
                value={totals.sdkTestPassedCount}
                positive
              />
            </Row>
          )}

          {/* Color legend */}
          <div className="d-flex flex-wrap gap-3 mb-2 small align-items-center text-muted">
            <strong>Key:</strong>
            <span>
              <Badge bg="success" className="me-1">
                <CheckCircle size={10} /> OK
              </Badge>
              All checks pass (DB + SDK test within {SDK_TEST_FRESH_DAYS} days)
            </span>
            <span>
              <Badge bg="warning" text="dark" className="me-1">
                <AlertTriangle size={10} /> Pending
              </Badge>
              DB checks pass — SDK end-to-end test required
            </span>
            <span>
              <Badge bg="danger" className="me-1">
                <XCircle size={10} /> Missing
              </Badge>
              Core data issues present
            </span>
            <span>
              <Badge bg="secondary" className="me-1">
                <AlertTriangle size={10} /> Error
              </Badge>
              System/infrastructure failure during snapshot — data unreliable,
              use Run missing to retry
            </span>
          </div>

          {/* Table */}
          {error && <Alert variant="danger">Failed to load snapshots.</Alert>}
          {totalSnapshotsPages > 1 && (
            <SnapshotPager
              page={snapshotsPage}
              totalPages={totalSnapshotsPages}
              total={snapshotsData?.total ?? 0}
              pageSize={SNAPSHOTS_PAGE_SIZE}
              onPage={goToSnapshotPage}
            />
          )}
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : (
            <Card>
              <Card.Body className="p-0">
                <Table size="sm" hover className="mb-0 small">
                  <thead className="table-light">
                    <tr>
                      <th>Domain</th>
                      <th>Country</th>
                      <th>Brand</th>
                      <th>Status</th>
                      <th>Deal</th>
                      <th>Auto</th>
                      <th>SKU</th>
                      <th>Sitewide</th>
                      <th>Offer</th>
                      <th title="Active coupons">Coupons</th>
                      <th title="With metadata / active">Metadata</th>
                      <th title="With targeting / active">Targeting</th>
                      <th title="Coupon-vs-metadata coverage mismatches">
                        Mismatch
                      </th>
                      <th title="SDK end-to-end test status">SDK Test</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 && (
                      <tr>
                        <td
                          colSpan={15}
                          className="text-center text-muted py-3"
                        >
                          No snapshots match the current filters.
                        </td>
                      </tr>
                    )}
                    {rows.map((r) => (
                      <tr key={r._id}>
                        <td>{r.domain}</td>
                        <td>{r.countryCode}</td>
                        <td>
                          {r.brandId ? (
                            <Link to={`/kindred/brands/${r.brandId}`}>
                              {r.brandName ?? r.brandId.slice(0, 8)}
                            </Link>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>{statusBadge(r.metrics.overallStatus)}</td>
                        <td>{yesNoBadge(r.metrics.hasDeal)}</td>
                        <td>
                          {r.metrics.hasAutoTemplate ? (
                            <Badge bg="success">
                              {r.metrics.autoTemplateCount}
                            </Badge>
                          ) : (
                            <Badge bg="danger">0</Badge>
                          )}
                        </td>
                        <td>
                          {r.metrics.hasSkuTemplate ? (
                            <Badge bg="success">
                              {r.metrics.skuTemplateCount}
                            </Badge>
                          ) : (
                            <Badge bg="danger">0</Badge>
                          )}
                        </td>
                        <td>
                          {r.metrics.hasAnySitewideCoupon ? (
                            <Badge bg="primary">
                              {r.metrics.sitewideCouponCount}
                            </Badge>
                          ) : (
                            <Badge bg="outline-secondary">0</Badge>
                          )}
                        </td>
                        <td>
                          {r.metrics.hasAnyOffer ? (
                            <Badge bg="warning" text="dark">
                              {r.metrics.offerCount}
                            </Badge>
                          ) : (
                            <Badge bg="outline-secondary">0</Badge>
                          )}
                        </td>
                        <td>{r.metrics.activeCouponCount}</td>
                        <td>
                          {r.metrics.couponsWithMetadataCount}/
                          {r.metrics.activeCouponCount}{" "}
                          <span className="text-muted">
                            ({fmtPct(r.metrics.metadataCoveragePct)})
                          </span>
                        </td>
                        <td>
                          {r.metrics.couponsWithTargetingCount}/
                          {r.metrics.activeCouponCount}{" "}
                          <span className="text-muted">
                            ({fmtPct(r.metrics.targetingCoveragePct)})
                          </span>
                        </td>
                        <td>
                          {r.metrics.couponMetadataCoverageMismatchCount > 0 ? (
                            <Badge bg="danger">
                              <AlertTriangle size={10} />{" "}
                              {r.metrics.couponMetadataCoverageMismatchCount}
                            </Badge>
                          ) : (
                            <Badge bg="success">0</Badge>
                          )}
                        </td>
                        <td>
                          {(r.metrics as any).lastSdkTest ? (
                            <>
                              {sdkCheckoutPills(r.metrics)}
                              {(r.metrics as any).lastSdkTestAt && (
                                <span
                                  className="text-muted"
                                  style={{ fontSize: 10 }}
                                >
                                  {Math.floor(
                                    (Date.now() -
                                      new Date(
                                        (r.metrics as any).lastSdkTestAt
                                      ).getTime()) /
                                      (1000 * 60 * 60 * 24)
                                  )}
                                  d ago
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-muted small">—</span>
                          )}
                        </td>
                        <td>
                          <div className="d-flex gap-1 align-items-center flex-nowrap">
                            <Link
                              to={`/kindred/full-auto-coupons-targeting/${r.qaItemId}`}
                              title="Open detail in a new tab"
                              className="text-decoration-none small"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Search size={12} /> Detail
                            </Link>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              className="py-0 px-1"
                              style={{ fontSize: 11 }}
                              title="Run this item now (async)"
                              disabled={runningItems.has(r.qaItemId)}
                              onClick={() => handleRunItem(r.qaItemId)}
                            >
                              {runningItems.has(r.qaItemId) ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <Play size={11} />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              className="py-0 px-1"
                              style={{ fontSize: 11 }}
                              title="Remove from tracking"
                              disabled={
                                removingItems.has(r.qaItemId) ||
                                !canManageTargeting
                              }
                              onClick={() => handleRemoveItem(r.qaItemId)}
                            >
                              {removingItems.has(r.qaItemId) ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <Trash2 size={11} />
                              )}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}

          {totalSnapshotsPages > 1 && (
            <SnapshotPager
              page={snapshotsPage}
              totalPages={totalSnapshotsPages}
              total={snapshotsData?.total ?? 0}
              pageSize={SNAPSHOTS_PAGE_SIZE}
              onPage={goToSnapshotPage}
            />
          )}
        </>
      )}

      {/* ── RUNS VIEW ── */}
      {view === "runs" && (
        <RunsTab
          runs={runsResp?.runs ?? []}
          activeRuns={activeRunsData ?? []}
          activeCount={activeCount}
          activeRunsLoaded={activeRunsData !== null}
          activeRunsLoading={activeRunsLoading}
          onLoadActiveRuns={loadActiveRuns}
          total={runsResp?.total ?? 0}
          page={runsResp?.page ?? runsPage}
          pageSize={runsResp?.pageSize ?? RUNS_PAGE_SIZE}
          loading={runsLoading}
          statusFilter={runsStatus}
          typeFilter={runsType}
          triggeredByFilter={runsTriggeredBy}
          dateFilter={runsDate}
          availableDates={runsResp?.availableDates ?? []}
          triggeredByOptions={runsResp?.triggeredByOptions ?? []}
          onStatusChange={setRunsStatus}
          onTypeChange={setRunsType}
          onTriggeredByChange={setRunsTriggeredBy}
          onDateChange={setRunsDate}
          onApply={applyRunsFilter}
          onClear={clearRunsFilter}
          onPage={goToRunsPage}
          selectedDetail={selectedRunDetail}
          detailLoading={runDetailLoading}
          onViewDetail={handleViewRunDetail}
          onClearDetail={() => setSelectedRunDetail(null)}
          onCancelRun={handleCancelRunById}
          cancellingRunId={cancellingRunId}
          canCancel={canManageTargeting}
        />
      )}

      {/* ── REPORTING VIEW ── */}
      {view === "reporting" && (
        <ReportingTab
          entries={reportResp?.entries ?? []}
          loading={reportLoading}
        />
      )}
    </div>
  );
};

const SnapshotPager: React.FC<{
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  onPage: (p: number) => void;
}> = ({ page, totalPages, total, pageSize, onPage }) => (
  <div className="d-flex justify-content-between align-items-center my-2 small text-muted">
    <span>
      Showing {Math.min((page - 1) * pageSize + 1, total)}–
      {Math.min(page * pageSize, total)} of {total}
    </span>
    <div className="d-flex gap-1">
      <Button
        size="sm"
        variant="outline-secondary"
        disabled={page === 1}
        onClick={() => onPage(1)}
      >
        «
      </Button>
      <Button
        size="sm"
        variant="outline-secondary"
        disabled={page === 1}
        onClick={() => onPage(page - 1)}
      >
        ‹
      </Button>
      <span className="px-2 d-flex align-items-center">
        Page {page} / {totalPages}
      </span>
      <Button
        size="sm"
        variant="outline-secondary"
        disabled={page === totalPages}
        onClick={() => onPage(page + 1)}
      >
        ›
      </Button>
      <Button
        size="sm"
        variant="outline-secondary"
        disabled={page === totalPages}
        onClick={() => onPage(totalPages)}
      >
        »
      </Button>
    </div>
  </div>
);

const SummaryTile: React.FC<{
  label: string;
  value: number;
  positive?: boolean;
  amber?: boolean;
  negative?: boolean;
}> = ({ label, value, positive, amber, negative }) => (
  <Col xs={6} md={3} lg>
    <Card className="h-100">
      <Card.Body className="py-2 text-center">
        <div className="text-muted small">{label}</div>
        <div
          className={`fs-5 fw-bold ${
            positive
              ? "text-success"
              : amber
                ? "text-warning"
                : negative
                  ? "text-danger"
                  : ""
          }`}
        >
          {value}
        </div>
      </Card.Body>
    </Card>
  </Col>
);

// ── Reporting Tab ────────────────────────────────────────

const REPORT_LINES: Array<{
  key: keyof IFullAutoCouponsTargetingDailyReportEntry;
  label: string;
  color: string;
}> = [
  {
    key: "totalAutoTemplates",
    label: "Total Auto Templates",
    color: "#0d6efd",
  },
  { key: "totalSkuTemplates", label: "Total SKU Templates", color: "#6610f2" },
  {
    key: "totalActiveAutoTemplates",
    label: "Active Auto Templates",
    color: "#198754",
  },
  {
    key: "totalActiveSkuTemplates",
    label: "Active SKU Templates",
    color: "#20c997",
  },
  {
    key: "totalActiveCombined",
    label: "Active Combined (Auto + SKU)",
    color: "#fd7e14",
  },
  {
    key: "totalActiveCombinedBrokenTests",
    label: "Combined — Broken Tests",
    color: "#dc3545",
  },
  {
    key: "totalActiveCombinedWithSitewideOrTargeting",
    label: "Combined — Sitewide or Targeting",
    color: "#0dcaf0",
  },
];

// ── Runs Tab ─────────────────────────────────────────────

const runStatusVariant = (
  status: IFullAutoCouponsTargetingRunSummary["status"]
) => {
  if (status === "completed") return "success";
  if (status === "running") return "primary";
  if (status === "pending") return "info";
  if (status === "cancelled") return "warning";
  return "danger";
};

const batchStatusVariant = (
  status: IFullAutoCouponsTargetingBatch["status"]
) => {
  if (status === "completed") return "success";
  if (status === "claimed") return "primary";
  if (status === "pending") return "secondary";
  if (status === "cancelled") return "warning";
  return "danger";
};

const fmtDuration = (ms: number) => {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const min = Math.floor(ms / 60_000);
  const sec = Math.floor((ms % 60_000) / 1000);
  return `${min}m ${sec}s`;
};

const RUN_STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "running", label: "Running" },
  { value: "pending", label: "Pending" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
];

const RUN_TYPE_OPTIONS = [
  { value: "", label: "All types" },
  { value: "bulk", label: "Bulk" },
  { value: "single", label: "Single" },
];

const RunsPager: React.FC<{
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  onPage: (p: number) => void;
}> = ({ page, totalPages, total, pageSize, onPage }) => (
  <div className="d-flex justify-content-between align-items-center my-1 small text-muted">
    <span>
      Showing {total === 0 ? 0 : Math.min((page - 1) * pageSize + 1, total)}–
      {Math.min(page * pageSize, total)} of {total} run
      {total !== 1 ? "s" : ""}
    </span>
    <div className="d-flex gap-1">
      <Button
        size="sm"
        variant="outline-secondary"
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
      >
        ‹
      </Button>
      {Array.from({ length: totalPages }, (_, i) => i + 1)
        .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
        .reduce<(number | "…")[]>((acc, p, idx, arr) => {
          if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push("…");
          acc.push(p);
          return acc;
        }, [])
        .map((p, i) =>
          p === "…" ? (
            <span key={`ellipsis-${i}`} className="px-1 align-self-center">
              …
            </span>
          ) : (
            <Button
              key={p}
              size="sm"
              variant={p === page ? "secondary" : "outline-secondary"}
              onClick={() => onPage(p as number)}
            >
              {p}
            </Button>
          )
        )}
      <Button
        size="sm"
        variant="outline-secondary"
        disabled={page >= totalPages}
        onClick={() => onPage(page + 1)}
      >
        ›
      </Button>
    </div>
  </div>
);

const RunDetailModal: React.FC<{
  detail: IFullAutoCouponsTargetingRunDetail | null;
  loading: boolean;
  onHide: () => void;
}> = ({ detail, loading, onHide }) => (
  <Modal show={loading || detail !== null} onHide={onHide} size="xl" scrollable>
    <Modal.Header closeButton>
      <Modal.Title className="fs-6">
        {detail
          ? `Run details — ${detail.run.snapshotDate} · ${detail.run.runId}`
          : "Loading…"}
      </Modal.Title>
    </Modal.Header>
    <Modal.Body className="p-0">
      {loading && (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading batch details…
        </div>
      )}
      {detail && (
        <>
          <div className="px-3 py-2 border-bottom small d-flex gap-3 flex-wrap text-muted">
            <span>
              Status:{" "}
              <Badge bg={runStatusVariant(detail.run.status)}>
                {detail.run.status}
              </Badge>
            </span>
            <span>
              Items: {detail.run.itemsProcessed}/{detail.run.itemsConsidered}
              {detail.run.itemsFailed > 0 && (
                <span className="text-danger ms-1">
                  ({detail.run.itemsFailed} failed)
                </span>
              )}
            </span>
            <span>
              Batches:{" "}
              <span className="text-success">
                {detail.run.completedBatches}
              </span>
              {detail.run.failedBatches > 0 && (
                <span className="text-danger">
                  /{detail.run.failedBatches}f
                </span>
              )}
              /{detail.run.totalBatches}
            </span>
            <span>
              Started: {new Date(detail.run.startedAt).toLocaleString()}
            </span>
            {detail.run.completedAt && (
              <span>Duration: {fmtDuration(detail.run.durationMs)}</span>
            )}
            {detail.run.triggeredBy && (
              <span>By: {detail.run.triggeredBy}</span>
            )}
          </div>
          <Table size="sm" hover className="mb-0 small">
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>Status</th>
                <th>Items</th>
                <th>Worker</th>
                <th>Started</th>
                <th>Completed</th>
                <th>Duration</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {detail.batches.map((b) => {
                const durationMs =
                  b.startedAt && b.completedAt
                    ? new Date(b.completedAt).getTime() -
                      new Date(b.startedAt).getTime()
                    : null;
                return (
                  <tr key={b.batchId}>
                    <td>{b.batchIndex}</td>
                    <td>
                      <Badge bg={batchStatusVariant(b.status)}>
                        {b.status}
                      </Badge>
                    </td>
                    <td>
                      {b.processedCount}/{b.totalItems}
                      {b.failedCount > 0 && (
                        <span className="text-danger ms-1">
                          ({b.failedCount}f)
                        </span>
                      )}
                    </td>
                    <td
                      className="font-monospace text-muted"
                      style={{ fontSize: 10 }}
                    >
                      {b.workerId ?? "—"}
                    </td>
                    <td className="text-muted">
                      {b.startedAt
                        ? new Date(b.startedAt).toLocaleTimeString()
                        : "—"}
                    </td>
                    <td className="text-muted">
                      {b.completedAt
                        ? new Date(b.completedAt).toLocaleTimeString()
                        : "—"}
                    </td>
                    <td className="text-muted">
                      {durationMs != null ? fmtDuration(durationMs) : "—"}
                    </td>
                    <td
                      className="text-danger"
                      style={{
                        maxWidth: 220,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={b.errorMessage ?? undefined}
                    >
                      {b.errorMessage ?? ""}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </>
      )}
    </Modal.Body>
    <Modal.Footer>
      <Button size="sm" variant="secondary" onClick={onHide}>
        Close
      </Button>
    </Modal.Footer>
  </Modal>
);

const RunsTab: React.FC<{
  runs: IFullAutoCouponsTargetingRunSummary[];
  activeRuns: IFullAutoCouponsTargetingRunSummary[];
  activeCount: number;
  activeRunsLoaded: boolean;
  activeRunsLoading: boolean;
  onLoadActiveRuns: () => void;
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  statusFilter: string;
  typeFilter: string;
  triggeredByFilter: string;
  dateFilter: string;
  availableDates: string[];
  triggeredByOptions: string[];
  onStatusChange: (v: string) => void;
  onTypeChange: (v: string) => void;
  onTriggeredByChange: (v: string) => void;
  onDateChange: (v: string) => void;
  onApply: () => void;
  onClear: () => void;
  onPage: (p: number) => void;
  selectedDetail: IFullAutoCouponsTargetingRunDetail | null;
  detailLoading: boolean;
  onViewDetail: (runId: string) => void;
  onClearDetail: () => void;
  onCancelRun: (runId: string) => void;
  cancellingRunId: string | null;
  canCancel: boolean;
}> = ({
  runs,
  activeRuns,
  activeCount,
  activeRunsLoaded,
  activeRunsLoading,
  onLoadActiveRuns,
  total,
  page,
  pageSize,
  loading,
  statusFilter,
  typeFilter,
  triggeredByFilter,
  dateFilter,
  availableDates,
  triggeredByOptions,
  onStatusChange,
  onTypeChange,
  onTriggeredByChange,
  onDateChange,
  onApply,
  onClear,
  onPage,
  selectedDetail,
  detailLoading,
  onViewDetail,
  onClearDetail,
  onCancelRun,
  cancellingRunId,
  canCancel,
}) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Collapsed by default; the active-runs list is only fetched on first expand.
  const [activeOpen, setActiveOpen] = useState(false);
  const toggleActive = () => {
    const next = !activeOpen;
    setActiveOpen(next);
    if (next && !activeRunsLoaded) onLoadActiveRuns();
  };

  const isActive = (run: IFullAutoCouponsTargetingRunSummary) =>
    run.status === "running" || run.status === "pending";

  const runActions = (run: IFullAutoCouponsTargetingRunSummary) => (
    <div className="d-flex gap-1 justify-content-end flex-nowrap">
      {isActive(run) && (
        <Button
          size="sm"
          variant="outline-danger"
          className="py-0 px-1"
          style={{ fontSize: 11 }}
          title={
            canCancel
              ? "Cancel this run"
              : "You don't have permission to cancel runs"
          }
          disabled={!canCancel || cancellingRunId === run.runId}
          onClick={() => onCancelRun(run.runId)}
        >
          {cancellingRunId === run.runId ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <>
              <XCircle size={11} className="me-1" />
              Cancel
            </>
          )}
        </Button>
      )}
      <Button
        size="sm"
        variant="outline-secondary"
        className="py-0 px-1"
        style={{ fontSize: 11 }}
        onClick={() => onViewDetail(run.runId)}
      >
        Details
      </Button>
    </div>
  );

  const runRow = (run: IFullAutoCouponsTargetingRunSummary) => (
    <tr key={run.runId}>
      <td className="font-monospace" style={{ fontSize: 11 }}>
        {run.runId}
      </td>
      <td>{run.snapshotDate}</td>
      <td>
        <Badge bg={runStatusVariant(run.status)}>{run.status}</Badge>
        {run.type === "single" && (
          <Badge bg="light" text="dark" className="ms-1 border">
            single
          </Badge>
        )}
      </td>
      <td className="text-muted">{run.triggeredBy ?? "—"}</td>
      <td>
        {run.itemsProcessed}/{run.itemsConsidered}
        {run.itemsFailed > 0 && (
          <span className="text-danger ms-1">({run.itemsFailed} failed)</span>
        )}
      </td>
      <td>
        <span className="text-success">{run.completedBatches}</span>
        {run.failedBatches > 0 && (
          <span className="text-danger">/{run.failedBatches}f</span>
        )}
        <span className="text-muted">/{run.totalBatches}</span>
      </td>
      <td className="text-muted">{new Date(run.startedAt).toLocaleString()}</td>
      <td className="text-muted">
        {run.completedAt
          ? fmtDuration(run.durationMs)
          : isActive(run)
            ? "in progress…"
            : "—"}
      </td>
      <td>{runActions(run)}</td>
    </tr>
  );

  const tableHead = (sticky = false) => (
    <thead className={`table-light${sticky ? " sticky-top" : ""}`}>
      <tr>
        <th>Run ID</th>
        <th>Date</th>
        <th>Status</th>
        <th>Triggered by</th>
        <th>Items</th>
        <th>Batches</th>
        <th>Started</th>
        <th>Duration</th>
        <th className="text-end">Actions</th>
      </tr>
    </thead>
  );

  return (
    <div>
      {/* ── Currently running / active — collapsed by default; the list is
           fetched only on first expand and capped in height so a long backlog
           of stuck runs never takes over the page. Shows every pending/running
           run regardless of the filters/date below. ── */}
      <Card className="mb-3 border-primary">
        <Card.Header
          role="button"
          onClick={toggleActive}
          style={{ cursor: "pointer" }}
          aria-expanded={activeOpen}
          className="py-2 d-flex align-items-center justify-content-between bg-primary bg-opacity-10"
        >
          <span className="fw-semibold small d-flex align-items-center">
            {activeOpen ? (
              <ChevronDown size={14} className="me-1 text-primary" />
            ) : (
              <ChevronRight size={14} className="me-1 text-primary" />
            )}
            <Activity size={14} className="me-1 text-primary" />
            Currently running
            {activeCount > 0 && (
              <Badge bg="primary" className="ms-2">
                {activeCount}
              </Badge>
            )}
          </span>
          <span className="text-muted" style={{ fontSize: 11 }}>
            {activeOpen
              ? "Shown regardless of the filters below"
              : activeCount > 0
                ? "Click to view / cancel"
                : "Click to expand"}
          </span>
        </Card.Header>
        {activeOpen && (
          <Card.Body className="p-0">
            {activeRunsLoading && !activeRunsLoaded ? (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" />
              </div>
            ) : activeRuns.length === 0 ? (
              <div className="text-center text-muted small py-3">
                Nothing running right now.
              </div>
            ) : (
              <div style={{ maxHeight: 260, overflowY: "auto" }}>
                <Table size="sm" hover className="mb-0 small">
                  {tableHead(true)}
                  <tbody>{activeRuns.map(runRow)}</tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        )}
      </Card>

      {/* ── Filters ── */}
      <Card className="mb-2">
        <Card.Body className="py-2">
          <Row className="g-2 align-items-end">
            <Col md={2}>
              <Form.Label className="small mb-0">Status</Form.Label>
              <Form.Select
                size="sm"
                value={statusFilter}
                onChange={(e) => onStatusChange(e.target.value)}
              >
                {RUN_STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="small mb-0">Type</Form.Label>
              <Form.Select
                size="sm"
                value={typeFilter}
                onChange={(e) => onTypeChange(e.target.value)}
              >
                {RUN_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label className="small mb-0">Triggered by</Form.Label>
              <Form.Select
                size="sm"
                value={triggeredByFilter}
                onChange={(e) => onTriggeredByChange(e.target.value)}
              >
                <option value="">Anyone</option>
                {triggeredByOptions.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="small mb-0">Snapshot date</Form.Label>
              <Form.Select
                size="sm"
                // "" is the transient pre-resolution default; show it as the
                // first concrete option ("All dates") to avoid a controlled
                // <select> value mismatch warning.
                value={dateFilter || "all"}
                onChange={(e) => onDateChange(e.target.value)}
              >
                <option value="all">All dates</option>
                {availableDates.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3} className="d-flex gap-2">
              <Button
                size="sm"
                variant="outline-primary"
                onClick={onApply}
                className="w-100"
              >
                <Search size={13} className="me-1" />
                Apply
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={onClear}
                className="w-100"
              >
                Clear
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ── History (filtered + paginated) ── */}
      <RunsPager
        page={page}
        totalPages={totalPages}
        total={total}
        pageSize={pageSize}
        onPage={onPage}
      />

      <Card className="mb-1">
        <Card.Body className="p-0">
          <Table size="sm" hover className="mb-0 small">
            {tableHead()}
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="text-center py-4">
                    <Spinner animation="border" size="sm" />
                  </td>
                </tr>
              ) : runs.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center text-muted py-3">
                    No runs match the current filters.
                  </td>
                </tr>
              ) : (
                runs.map(runRow)
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <RunsPager
        page={page}
        totalPages={totalPages}
        total={total}
        pageSize={pageSize}
        onPage={onPage}
      />

      <RunDetailModal
        detail={selectedDetail}
        loading={detailLoading}
        onHide={onClearDetail}
      />
    </div>
  );
};

// The daily report must never span more than the most recent 28 days.
const DAILY_REPORT_MAX_DAYS = 28;

const ReportingTab: React.FC<{
  entries: IFullAutoCouponsTargetingDailyReportEntry[];
  loading: boolean;
}> = ({ entries: allEntries, loading }) => {
  const [visibleLines, setVisibleLines] = useState<Set<string>>(
    new Set(REPORT_LINES.map((l) => l.key))
  );
  const [exporting, setExporting] = useState(false);

  // Cap to the last 28 days regardless of how many the API returns.
  const entries = allEntries.slice(-DAILY_REPORT_MAX_DAYS);

  const latest = entries[entries.length - 1] ?? null;

  const { execute: fetchExportCsv } = useGetLazy<string>();

  const toggleLine = useCallback((key: string) => {
    setVisibleLines((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const csvText = await fetchExportCsv(
        "full-auto-coupons-targeting/report/export",
        { responseType: "text" }
      );
      if (!csvText) return;
      const blob = new Blob([csvText], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `full-auto-coupons-report-domains-${latest?.date ?? new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <Alert variant="secondary" className="small">
        No report data available yet. Run a snapshot first.
      </Alert>
    );
  }

  return (
    <div>
      {/* ── Snapshot cards ── */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0 text-muted">
          Latest snapshot:{" "}
          <strong className="text-dark">{latest?.date ?? "—"}</strong>
        </h6>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={handleExportCsv}
          disabled={entries.length === 0 || exporting}
          title="Export per-domain metric flags for the latest snapshot date"
        >
          {exporting ? (
            <Spinner animation="border" size="sm" className="me-1" />
          ) : (
            <Download size={13} className="me-1" />
          )}
          Export domains CSV
        </Button>
      </div>

      {latest && (
        <Row className="g-2 mb-4">
          <ReportCard
            label="Total Auto Templates"
            value={latest.totalAutoTemplates}
            sub="Total template instances across all tracked items"
            color="primary"
          />
          <ReportCard
            label="Total SKU Templates"
            value={latest.totalSkuTemplates}
            sub="Total SKU template instances across all tracked items"
            color="indigo"
          />
          <ReportCard
            label="Active Auto Templates"
            value={latest.totalActiveAutoTemplates}
            sub="Items with at least one auto template"
            color="success"
          />
          <ReportCard
            label="Active SKU Templates"
            value={latest.totalActiveSkuTemplates}
            sub="Items with at least one SKU template"
            color="teal"
          />
          <ReportCard
            label="Active Combined"
            value={latest.totalActiveCombined}
            sub="Items with both Auto + SKU templates"
            color="warning"
          />
          <ReportCard
            label="Combined — Broken Tests"
            value={latest.totalActiveCombinedBrokenTests}
            sub="Combined items whose SDK test is not green"
            color="danger"
          />
          <ReportCard
            label="Combined — Sitewide or Targeting"
            value={latest.totalActiveCombinedWithSitewideOrTargeting}
            sub="Combined items with sitewide coupon or targeting"
            color="cyan"
          />
        </Row>
      )}

      {/* ── Trend chart ── */}
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center py-2">
          <span className="fw-semibold small">Daily trends</span>
          <div className="d-flex flex-wrap gap-2">
            {REPORT_LINES.map((l) => (
              <button
                key={l.key}
                onClick={() => toggleLine(l.key)}
                className="border-0 bg-transparent p-0 small d-flex align-items-center gap-1"
                style={{
                  opacity: visibleLines.has(l.key) ? 1 : 0.35,
                  cursor: "pointer",
                }}
              >
                <span
                  style={{
                    display: "inline-block",
                    width: 12,
                    height: 3,
                    background: l.color,
                    borderRadius: 2,
                  }}
                />
                {l.label}
              </button>
            ))}
          </div>
        </Card.Header>
        <Card.Body className="p-2">
          <ResponsiveContainer width="100%" height={320}>
            <LineChart
              data={entries}
              margin={{ top: 4, right: 16, bottom: 0, left: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => v.slice(5)} // show MM-DD
              />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ fontSize: 12 }}
                labelFormatter={(l) => `Date: ${l}`}
              />
              {REPORT_LINES.filter((l) => visibleLines.has(l.key)).map((l) => (
                <Line
                  key={l.key}
                  type="monotone"
                  dataKey={l.key}
                  name={l.label}
                  stroke={l.color}
                  strokeWidth={2}
                  dot={entries.length <= 30}
                  activeDot={{ r: 4 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </Card.Body>
      </Card>

      {/* ── Data table ── */}
      <Card>
        <Card.Header className="py-2">
          <span className="fw-semibold small">
            Daily breakdown ({entries.length} days)
          </span>
        </Card.Header>
        <Card.Body className="p-0">
          <div style={{ maxHeight: 400, overflowY: "auto" }}>
            <Table size="sm" hover className="mb-0 small">
              <thead className="table-light sticky-top">
                <tr>
                  <th>Date</th>
                  <th title="Total tracked items">Items</th>
                  <th title="Sum of autoTemplateCount">Auto Tpl</th>
                  <th title="Sum of skuTemplateCount">SKU Tpl</th>
                  <th title="Items with hasAutoTemplate=true">Active Auto</th>
                  <th title="Items with hasSkuTemplate=true">Active SKU</th>
                  <th title="Items with both Auto + SKU">Combined</th>
                  <th title="Combined with broken SDK test">Broken Tests</th>
                  <th title="Combined with Sitewide or Targeting">
                    Sitewide/Targeting
                  </th>
                </tr>
              </thead>
              <tbody>
                {[...entries].reverse().map((e) => (
                  <tr key={e.date}>
                    <td className="fw-semibold">{e.date}</td>
                    <td>{e.totalItems}</td>
                    <td>{e.totalAutoTemplates}</td>
                    <td>{e.totalSkuTemplates}</td>
                    <td>
                      <Badge bg="success">{e.totalActiveAutoTemplates}</Badge>
                    </td>
                    <td>
                      <Badge bg="primary">{e.totalActiveSkuTemplates}</Badge>
                    </td>
                    <td>
                      <Badge bg="warning" text="dark">
                        {e.totalActiveCombined}
                      </Badge>
                    </td>
                    <td>
                      {e.totalActiveCombinedBrokenTests > 0 ? (
                        <Badge bg="danger">
                          {e.totalActiveCombinedBrokenTests}
                        </Badge>
                      ) : (
                        <Badge bg="success">0</Badge>
                      )}
                    </td>
                    <td>
                      <Badge bg="info">
                        {e.totalActiveCombinedWithSitewideOrTargeting}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

const ReportCard: React.FC<{
  label: string;
  value: number;
  sub: string;
  color: string;
}> = ({ label, value, sub, color }) => (
  <Col xs={6} sm={4} md={3} xl>
    <Card className="h-100">
      <Card.Body className="py-2 px-3">
        <div
          className="small fw-semibold mb-1"
          style={{
            fontSize: "0.72rem",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          {label}
        </div>
        <div className={`fs-4 fw-bold text-${color}`}>{value}</div>
        <div className="text-muted" style={{ fontSize: "0.7rem" }}>
          {sub}
        </div>
      </Card.Body>
    </Card>
  </Col>
);
